import type { GridProps } from '@mui/material';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { COLORS } from 'muiTheme/colors';
import { SIZES, SPACING } from 'muiTheme/MuiDataGrid';
import type { MouseEventHandler, ReactNode } from 'react';
import { TotalRowTypesIdentifier } from '../../../grid/reduxStore/table.types.ts';
import { formatPriceWithDollarSign } from '../Table/helper.ts';
import { getRoundedValueTooltipTitle } from '../Table/components/CustomTableCell.tsx';
import { getRowClassName } from '../../../../../muiTheme/dataGridUtils.ts';

const pseudoElement = {
  content: "''",
  display: 'block',
  position: 'absolute',
  width: '100%',
  height: '2px',
};

const FooterRowWrapper = styled(Grid)(() => ({
  columnSpacing: 2,
  minHeight: SIZES.defaultRowHeight,
  justifyContent: 'center',
  alignItems: 'stretch',
  borderLeft: `0.1px solid ${COLORS.borderBottomColor}`,
  borderRight: `0.1px solid ${COLORS.borderBottomColor}`,
  position: 'relative',
  '.tiptap p, &': {
    fontWeight: 600,
    color: COLORS.subtotalTaxDiscount,
  },
  '&.py-hide-total-row, &.py-hide-total-row:hover': {
    backgroundColor: COLORS.greyedBackgroundColor,
  },

  '&:last-child': {
    borderBottom: 0,
    borderColor: 'transparent',
  },

  '&.selected-row, &.selected-row + .unselected-row': {
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30), 0px 1px 3px rgba(0, 0, 0, 0.15)',
  },

  '&.selected-row': {
    '.py-active-row': {
      '&::before': {
        ...pseudoElement,
        backgroundColor: `${COLORS.HoverNFocus}`,
        top: 0,
        left: 0,
      },
      '&::after': {
        ...pseudoElement,
        backgroundColor: `${COLORS.HoverNFocus}`,
        bottom: 1,
        left: 0,
      },
      borderRight: `2px solid ${COLORS.HoverNFocus}`,
      borderLeft: `2px solid ${COLORS.HoverNFocus}`,
      position: 'relative',
    },
  },
  '&::before, &::after': {
    ...pseudoElement,
  },

  '&:hover::before, &:hover::after': {
    backgroundColor: COLORS.HoverNFocus,
  },
  '&::before': {
    top: 0,
  },
  '&::after': {
    bottom: 0,
    backgroundColor: COLORS.borderBottomColor,
  },
}));

const FooterRowItem = ({
  children,
  testId,
  onClick,
  className,
  ...props
}: {
  children?: ReactNode;
  testId?: string;
  className?: string;
  onClick?: MouseEventHandler;
} & GridProps) => (
  <Grid
    item
    sm={3}
    alignItems="center"
    className={className}
    data-testid={testId}
    display="flex"
    justifyContent="flex-end"
    onClick={onClick}
    textAlign="right"
    width="100%"
    {...props}
  >
    {children}
  </Grid>
);

type PreviewPricingTableTotalFooterRowProps = {
  rowType: TotalRowTypesIdentifier;
  subtotalValue: number | string;
  onClick?: MouseEventHandler;
  isRowSelected: boolean;
  priceValue: string;
  pricingDecimalPlaces: number;
  calcInputComponent: ReactNode;
  titleComponent?: ReactNode;
  currencySign?: string;
  testIdOverride?: string;
};

type FooterRowItem = {
  key: string;
  testId?: string;
  children?: ReactNode;
  className?: string;
  props?: GridProps;
};

const PreviewPricingTableTotalFooterRow = ({
  rowType,
  subtotalValue,
  isRowSelected,
  priceValue,
  pricingDecimalPlaces,
  calcInputComponent,
  titleComponent,
  currencySign,
  testIdOverride,
}: PreviewPricingTableTotalFooterRowProps) => {
  const computeFormattedValue = () => {
    const hasValidSubtotal = typeof subtotalValue === 'number' ? !Number.isNaN(subtotalValue) : subtotalValue !== '';
    const hasValidSubtotalAndPrice = hasValidSubtotal && priceValue !== '';
    const isCalculatedRow = rowType === TotalRowTypesIdentifier.SUBTOTAL || rowType === TotalRowTypesIdentifier.TOTAL;
    const shouldFormat = isCalculatedRow || hasValidSubtotalAndPrice;

    return shouldFormat ? formatPriceWithDollarSign(subtotalValue, pricingDecimalPlaces, currencySign) : '';
  };

  const testIdValue = testIdOverride ?? rowType.toLowerCase();

  const valueItem: FooterRowItem = {
    key: 'value',
    testId: `value-${testIdValue}`,
    children: (
      <Tooltip
        data-testid="footer-cell-tooltip"
        title={getRoundedValueTooltipTitle({ value: subtotalValue, numberOfDecimalPlaces: pricingDecimalPlaces })}
        placement="bottom"
        followCursor
      >
        <span className="py-numeric-cell-value">{computeFormattedValue()}</span>
      </Tooltip>
    ),
    props: { paddingLeft: SPACING.sm, paddingRight: SPACING.sm },
  };

  const twoColumnFooterItems: FooterRowItem[] = [
    {
      key: 'title',
      testId: `title-${testIdValue}`,
      className: 'py-footer-cell py-footer-title-cell',
      children: (
        <>
          {titleComponent}
          {calcInputComponent}
        </>
      ),
      props: { sm: 9.2 },
    },
    { ...valueItem, className: 'py-footer-cell', props: { ...valueItem.props, sm: 2.8 } },
  ];

  const footerItems: FooterRowItem[] = twoColumnFooterItems;

  return (
    <FooterRowWrapper
      container
      data-testid={`${testIdValue}-row`}
      className={`${isRowSelected ? 'selected-row' : 'unselected-row'} ${getRowClassName(rowType)}`}
    >
      {footerItems.map(({ key, testId, children, className, props = {} }) => (
        <FooterRowItem className={className} key={key} testId={testId} {...props}>
          {children}
        </FooterRowItem>
      ))}
    </FooterRowWrapper>
  );
};

export default PreviewPricingTableTotalFooterRow;
